/**
 * Breadcrumb - Hierarchical navigation display
 *
 * Shows the path from root to currently selected structure.
 * Each ancestor is clickable to navigate up the hierarchy.
 */

/**
 * Breadcrumb class - manages ancestor path navigation
 */
export class Breadcrumb {
  /**
   * Create a new Breadcrumb
   * @param {string|HTMLElement} container - Container element or ID
   * @param {import('../map/CellMap.js').CellMap} cellMap - The CellMap instance
   */
  constructor(container, cellMap) {
    this.container = typeof container === 'string'
      ? document.getElementById(container)
      : container;
    this.cellMap = cellMap;
    this.currentAncestors = [];

    this.render();
  }

  /**
   * Render the breadcrumb HTML
   * @private
   */
  render() {
    this.element = document.createElement('nav');
    this.element.className = 'breadcrumb';
    this.element.setAttribute('aria-label', 'Strukturhierarchie');
    this.element.innerHTML = `
      <ol class="breadcrumb-list"></ol>
    `;

    this.listEl = this.element.querySelector('.breadcrumb-list');
    this.container.appendChild(this.element);
  }

  /**
   * Update breadcrumb with new ancestor tree
   * @param {Array} ancestors - Array of {id, name} objects from root to current
   */
  update(ancestors) {
    if (!ancestors || ancestors.length === 0) {
      this.clear();
      return;
    }

    this.currentAncestors = ancestors;
    this.listEl.innerHTML = '';

    ancestors.forEach((ancestor, index) => {
      const isLast = index === ancestors.length - 1;
      const li = this.createItem(ancestor, isLast);
      this.listEl.appendChild(li);
    });

    this.element.classList.add('breadcrumb--visible');
  }

  /**
   * Create a breadcrumb item
   * @param {Object} ancestor - {id, name}
   * @param {boolean} isLast - Whether this is the current item
   * @returns {HTMLElement}
   * @private
   */
  createItem(ancestor, isLast) {
    const li = document.createElement('li');
    li.className = 'breadcrumb-item';

    if (isLast) {
      // Current item - not clickable
      li.innerHTML = `
        <span class="breadcrumb-current" aria-current="page">
          ${this.escapeHtml(ancestor.name)}
        </span>
      `;
    } else {
      // Ancestor - clickable
      li.innerHTML = `
        <button class="breadcrumb-link" data-id="${ancestor.id}" type="button">
          ${this.escapeHtml(ancestor.name)}
        </button>
        <span class="breadcrumb-separator" aria-hidden="true">&rsaquo;</span>
      `;

      // Add click handler
      const button = li.querySelector('button');
      button.addEventListener('click', () => this.navigateTo(ancestor.id));
    }

    return li;
  }

  /**
   * Navigate to an ancestor structure
   * @param {number} structureId - ID of the structure to navigate to
   */
  navigateTo(structureId) {
    if (!this.cellMap) {
      return;
    }

    const clickHandler = this.cellMap.getClickHandler();
    if (clickHandler) {
      clickHandler.selectById(structureId);
    }
  }

  /**
   * Clear the breadcrumb
   */
  clear() {
    this.currentAncestors = [];
    this.listEl.innerHTML = '';
    this.element.classList.remove('breadcrumb--visible');
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped HTML
   * @private
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Get the current ancestor path
   * @returns {Array} Current ancestors
   */
  getCurrentPath() {
    return this.currentAncestors;
  }

  /**
   * Check if breadcrumb is visible
   * @returns {boolean}
   */
  isVisible() {
    return this.element.classList.contains('breadcrumb--visible');
  }

  /**
   * Destroy the breadcrumb and clean up
   */
  destroy() {
    this.element.remove();
  }
}
